import React, { useRef, useState } from 'react';
import { useInView } from '@/hooks/useInView';
import { hapticTap } from '@/utils/haptics';
import { s } from './styles';
import { IconCheck } from './icons';

// Reusable atoms for the Foundation Lesson screens. Extracted from
// FoundationLesson.jsx so per-screen modules can compose them without
// reaching back into the orchestrator.

export function BulletCard({ icon, children, className, style }) {
  return (
    <div className={className} style={{ ...s.bulletCard, ...style }}>
      <div style={s.bulletIconDisc}>{icon}</div>
      <div style={s.bulletText}>{children}</div>
    </div>
  );
}

export function Callout({ children, extra }) {
  return (
    <div style={s.callout}>
      <div>{children}</div>
      {extra && <div style={s.calloutExtra}>{extra}</div>}
    </div>
  );
}

// IntersectionObserver-gated step for the Gentle Push lesson. Only
// attaches the motion classes once the node has entered the viewport
// so reveals feel tied to scroll rather than firing below the fold.
// The observer disconnects after the first intersection (once: true)
// so the step stays visible if the user scrolls back past it.
export function InViewStep({ index, isMarble, children }) {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.3, once: true });
  return (
    <div
      ref={ref}
      className={inView ? 'stagger-item' : undefined}
      style={{
        ...s.pushStep,
        // Each step animates as it enters the viewport, so the
        // stagger index is reset to 0 — we want no delay beyond the
        // scroll-intersection moment itself.
        '--stagger-i': 0,
        opacity: inView ? undefined : 0,
      }}
    >
      <div style={s.pushStepIndex} aria-hidden="true">{index + 1}</div>
      {isMarble && inView && <MarbleDot />}
      {children}
    </div>
  );
}

// Rose-gradient marble: the visual anchor of the "picture this" step.
// Absolute-positioned onto the thread at step 4. 14px circle with a
// soft inner highlight (top-left) and a rose-to-plum radial — reads as
// a glass marble, not a disc. Drop-in animation runs when the parent
// step enters the viewport, via .pushMarble in motion.css.
export function MarbleDot() {
  return (
    <svg
      className="pushMarble"
      style={s.pushStepMarbleDot}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="rpMarbleGrad" cx="38%" cy="34%" r="70%">
          <stop offset="0%" stopColor="#fce4dc" />
          <stop offset="55%" stopColor="var(--primary-light, #d08a9e)" />
          <stop offset="100%" stopColor="var(--primary, #A8003F)" />
        </radialGradient>
      </defs>
      <circle cx="7" cy="7" r="6" fill="url(#rpMarbleGrad)" />
    </svg>
  );
}

export function SignalSection({ label, bullets, accent, icons }) {
  return (
    <div style={s.signalSection}>
      <div style={{ ...s.signalLabel, color: accent }}>{label}</div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {bullets.map((b, i) => (
          <li
            key={i}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              background: 'var(--surface, #FFFFFF)',
              border: '1px solid var(--border, #E8DDD4)',
              borderRadius: 12,
              padding: '12px 14px',
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'var(--primary-pale, #F5D6E0)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {icons && icons[i] ? icons[i] : <IconCheck />}
            </span>
            <span
              style={{
                flex: 1,
                fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
                fontSize: 15,
                lineHeight: 1.55,
                color: 'var(--dark-brown, #5A3E2B)',
                paddingTop: 4,
              }}
            >
              {b}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Optional 1-tap clarity check shown above the CTA on each teaching
// screen. Three chips: clear / mostly / not_yet. Selection is recorded
// into user_progress.data.fl_signals[screenId] but never blocks the
// CTA — a user who skips the check still proceeds via Continue. Each
// selection now lands with a soft response: a tiny one-shot cue
// (sprout / settle / a kind line) plus a warm acknowledgement, so the
// chips feel like a conversation rather than a survey.
export function FeltSenseCheck({ initialValue, onChange, t }) {
  // Local state keeps the chip selection snappy regardless of how
  // the upstream userProgress write resolves. Initial value lets a
  // user who steps back to a screen see their previous answer
  // already filled in (provided the parent keys this component on
  // currentScreen — which it does — so back-stepping remounts and
  // re-seeds from userProgress).
  const [selected, setSelected] = useState(initialValue || null);
  // pickToken increments on every fresh tap so the response cue
  // remounts and replays even when the user picks the same chip
  // twice. We also seed it from initialValue so the cue does not
  // replay on a back-step remount.
  const [pickToken, setPickToken] = useState(initialValue ? -1 : 0);
  const options = [
    { id: 'clear', label: t('fl_check_clear') },
    { id: 'mostly', label: t('fl_check_mostly') },
    { id: 'not_yet', label: t('fl_check_notyet') },
  ];
  function handlePick(id) {
    hapticTap();
    setSelected(id);
    setPickToken((n) => n + 1);
    if (onChange) onChange(id);
  }
  // Response cue per choice: a tiny emoji glyph that lifts (sprout)
  // or settles, plus a single warm line of acknowledgement. Every
  // option now lands warmly, not just "not yet".
  const cueGlyph = selected === 'clear' ? '✨' /* sparkles */
    : selected === 'mostly' ? '·' /* middle dot, soft "noted" */
    : selected === 'not_yet' ? '○' /* open circle, room to come back */
    : '';
  const cueClass = selected === 'clear' ? 'onboard-felt-cue--sprout'
    : selected === 'not_yet' ? 'onboard-felt-cue--settle'
    : 'onboard-felt-cue--sprout';
  const responseKey = selected === 'clear' ? 'fl_check_response_clear'
    : selected === 'mostly' ? 'fl_check_response_mostly'
    : selected === 'not_yet' ? 'fl_check_revisit_hint'
    : null;
  return (
    <div style={s.checkBlock}>
      <div style={s.checkPrompt}>{t('fl_check_prompt')}</div>
      <div style={s.checkRow} role="radiogroup" aria-label={t('fl_check_prompt')}>
        {options.map((o) => {
          const isSelected = selected === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => handlePick(o.id)}
              style={{ ...s.checkChip, ...(isSelected ? s.checkChipSelected : null) }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {responseKey && pickToken > 0 && (
        <div
          key={`${selected}-${pickToken}`}
          style={s.checkHint}
          aria-live="polite"
        >
          {t(responseKey)}
          <span aria-hidden="true" className={`onboard-felt-cue ${cueClass}`}>
            {cueGlyph}
          </span>
        </div>
      )}
    </div>
  );
}
